import moment from "moment";
import React, { ReactElement, useEffect, useState } from "react";
import Avatar from "react-avatar";
import { Col, FormControl, Row } from "react-bootstrap";
import { useHistory, useParams } from "react-router";
import Board from "react-trello";
import {
	getColorForTaskStatus,
	Project,
	ProjectTask,
	ProjectTaskDueDate,
	ProjectTaskModal,
	ProjectTaskStatus,
	useProjects,
} from ".";
import { Loading } from "../ui";
import { ProjectTaskLabelsList } from "./ProjectTaskLabelsList";

interface IProps {
	project: Project;
}

export const ProjectBoard = (props: IProps): ReactElement => {
	const [shownTask, setShownTask] = useState<ProjectTask | null>();
	const [searchTerm, setSearchTerm] = useState();

	const projectContext = useProjects();
	const { taskId } = useParams();
	const history = useHistory();

	if (!props.project) {
		return <strong>"Invalid Project"</strong>;
	}

	const formatTaskForBoard = (tasksData: ProjectTask[]) => {
		const data = Object.values(ProjectTaskStatus).map((status, index) => {
			return {
				id: `lane${index}`,
				title: status,
				label: "",
				cardStyle: {
					borderTop: `3px solid ${getColorForTaskStatus(status)}`,
				},
				cards: [] as ReactTrello.Card[],
			};
		});

		tasksData.forEach(
			(task: ProjectTask): void => {
				const lane = data.find(obj => {
					return obj.title === task.status;
				});

				if (lane) {
					const memberAvatars = task.members.map((member, index) => (
						<Avatar
							round
							key={index}
							name={member.user ? member.user.fullName : "NA"}
							size="25"
							textSizeRatio={2}
							color="#dfe1e6"
							fgColor="#172b4d"
							style={{ marginRight: 8 }}
						/>
					));
					const cover = task.cover();

					const description = (
						<>
							{cover ? (
								<img src={cover} className="task-card-image" />
							) : (
								""
							)}
							<div className="card-wrapper">
								{task.labels.length ? (
									<ProjectTaskLabelsList
										task={task}
										fontSize={8}
										paddingHorizontal={5}
										paddingVertical={2}
									/>
								) : (
									""
								)}

								<span className="task-title">{task.name}</span>
								<Row>
									<Col>
										<div
											style={{
												marginTop: 10,
												marginBottom: 10,
											}}
										>
											{memberAvatars}
										</div>
										<span style={{ opacity: 0.5 }}>
											#{task.id}
										</span>
									</Col>
									<Col
										style={{
											alignSelf: "flex-end",
											textAlign: "right",
										}}
									>
										<ProjectTaskDueDate
											task={task}
											dateFormat="MMM DD"
										/>
									</Col>
								</Row>
							</div>
						</>
					);

					lane.cards.push({
						id: String(task.id),
						title: "",
						label: moment(task.createdDate).fromNow(true),
						description,
						metadata: { task },
					});
				}
			},
		);

		return {
			lanes: data,
		};
	};

	const fetchProjectTasks = async (term?: string) => {
		await projectContext.findTasks!(term);
	};

	useEffect(() => {
		fetchProjectTasks(searchTerm);
	}, [searchTerm]);

	useEffect(() => {
		if (taskId) {
			if (projectContext.tasks) {
				const tid = parseInt(taskId, 10);
				const openTask = projectContext.tasks.filter(t => t.id === tid);
				if (openTask[0]) {
					setShownTask(openTask[0]);
				}
			}
		}
	}, [projectContext.tasks]);

	const handleMoveCard = (
		cardId: string,
		_sourceLandId: string,
		targetLaneId: string,
		position: number,
	) => {
		const lane = boardData.lanes.find(l => {
			return l.id === targetLaneId;
		});

		if (lane) {
			projectContext.changeTaskStatus!(
				parseInt(cardId, 10),
				position,
				lane.title,
			);
		}
	};

	const handleAddCard = (card: ReactTrello.Card, laneId: string) => {
		const lane = boardData.lanes.find(l => {
			return l.id === laneId;
		});

		if (lane) {
			projectContext.createTask!(
				card.title,
				card.description || "",
				lane.title,
			);
		}
	};

	const handleCardClick = (_cardId: string, metadata: any) => {
		setShownTask(metadata.task);
	};

	if (!projectContext.tasks) {
		return <Loading />;
	}

	const boardData = formatTaskForBoard(projectContext.tasks);

	let taskModal = null;

	if (shownTask) {
		taskModal = (
			<ProjectTaskModal
				task={shownTask}
				show={true}
				onHide={() => {
					setShownTask(null);
					history.push(`/projects/${projectContext.project!.id}`);
				}}
			/>
		);
	}

	return (
		<>
			{taskModal}
			<FormControl
				style={{ marginLeft: 20 }}
				placeholder="Filter tasks..."
				onChange={(e: any) => setSearchTerm(e.target.value)}
			/>
			<Board
				editable={true}
				data={boardData}
				handleDragEnd={handleMoveCard}
				onCardAdd={handleAddCard}
				onCardClick={handleCardClick}
				hideCardDeleteIcon={true}
				style={{ backgroundColor: "transparent" }}
			/>
		</>
	);
};
